import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

const STATUS_COLORS = { trial: '#F59E0B', active: '#00C853', suspended: '#EF4444' };
const STATUS_LABELS = { trial: 'Trial', active: 'Activo', suspended: 'Suspendido' };
const PLAN_LABELS = { starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };
const PLAN_PRICES = { starter: 75, growth: 119, enterprise: 249 };
const PLAN_SELLER_LIMITS = { starter: 5, growth: 10, enterprise: 20 };
const ADDON_PRICES = { coaching: 29 };
const TABS = ['empresas', 'costos', 'ingresos', 'leads'];
const LEAD_STATUS_COLORS = { new: '#F59E0B', contacted: '#3B82F6', qualified: '#00C853', discarded: '#EF4444' };
const LEAD_STATUS_LABELS = { new: 'Nuevo', contacted: 'Contactado', qualified: 'Calificado', discarded: 'Descartado' };

const Toggle = ({ on, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: 40, height: 22, borderRadius: 11, border: 'none', cursor: disabled ? 'default' : 'pointer',
    background: on ? '#00C853' : '#2A2A2A', position: 'relative', flexShrink: 0, opacity: disabled ? 0.5 : 1
  }}>
    <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left 0.2s' }}/>
  </button>
);

const Modal = ({ show, onClose, children }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: '#1A1A1A', borderRadius: 14, border: '1px solid #2A2A2A', padding: 28, maxWidth: 480, width: '100%' }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

export default function SuperAdmin() {
  const [workspaces, setWorkspaces] = useState([]);
  const [sellerCounts, setSellerCounts] = useState({});
  const [usage, setUsage] = useState([]);
  const [demoLeads, setDemoLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(null);
  const [activeTab, setActiveTab] = useState('empresas');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [editModal, setEditModal] = useState(null);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (activeTab === 'costos' || activeTab === 'ingresos') loadUsage(); }, [activeTab, selectedMonth]);
  useEffect(() => { if (activeTab === 'leads') loadDemoLeads(); }, [activeTab]);

  const loadData = async () => {
    try {
      const res = await api.get('/workspaces/all');
      setWorkspaces(res.data);
      const counts = {};
      await Promise.all(res.data.map(async ws => {
        try {
          const usersRes = await api.get(`/users/workspace/${ws.id}`);
          counts[ws.id] = usersRes.data.filter(u => u.role === 'seller' && u.active !== false).length;
        } catch (e) { counts[ws.id] = 0; }
      }));
      setSellerCounts(counts);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadUsage = async () => {
    try {
      const res = await api.get(`/usage/all?month=${selectedMonth}`);
      setUsage(res.data);
    } catch (e) { console.error(e); }
  };

  const loadDemoLeads = async () => {
    try {
      const res = await api.get('/demo-leads');
      setDemoLeads(res.data);
    } catch (e) { console.error(e); }
  };

  const updateDemoLead = async (id, data) => {
    try {
      await api.put(`/demo-leads/${id}`, data);
      setDemoLeads(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
    } catch (e) { alert('Error actualizando lead'); }
  };

  const handleStatus = async (id, status) => {
    setSaving(id);
    try {
      await api.put(`/workspaces/${id}/status`, { status });
      setWorkspaces(prev => prev.map(w => w.id === id
        ? { ...w, active: status !== 'suspended', billing: { ...w.billing, status } } : w));
    } catch (e) { alert('Error actualizando estado'); }
    finally { setSaving(null); }
  };

  const handleFeature = async (id, feature, value) => {
    setSaving(id + feature);
    try {
      const ws = workspaces.find(w => w.id === id);
      const features = { ...ws.billing?.features, [feature]: value };
      await api.put(`/workspaces/${id}/features`, { features });
      setWorkspaces(prev => prev.map(w => w.id === id
        ? { ...w, billing: { ...w.billing, features } } : w));
    } catch (e) { alert('Error actualizando feature'); }
    finally { setSaving(null); }
  };

  const handlePlan = async (id, plan) => {
    try {
      const sellerLimit = PLAN_SELLER_LIMITS[plan];
      await api.put(`/workspaces/${id}/features`, { plan, sellerLimit });
      setWorkspaces(prev => prev.map(w => w.id === id
        ? { ...w, billing: { ...w.billing, plan, sellerLimit } } : w));
    } catch (e) { alert('Error actualizando plan'); }
  };

  const handleMasterSwitch = async (id, value) => {
    setSaving(id + 'master');
    try {
      await api.put(`/workspaces/${id}/features`, { masterEnabled: value });
      if (!value) await api.put(`/workspaces/${id}/status`, { status: 'suspended' });
      setWorkspaces(prev => prev.map(w => w.id === id
        ? { ...w, active: value, billing: { ...w.billing, masterEnabled: value, status: value ? w.billing.status : 'suspended' } } : w));
    } catch (e) { alert('Error actualizando switch maestro'); }
    finally { setSaving(null); }
  };

  const handleEditCustom = async () => {
    if (!editModal) return;
    setSaving(editModal.ws.id + 'custom');
    try {
      const { ws, sellerLimit, customAmount } = editModal;
      await api.put(`/workspaces/${ws.id}/features`, {
        sellerLimit: parseInt(sellerLimit) || PLAN_SELLER_LIMITS[ws.billing?.plan || 'starter'],
        customAmount: customAmount ? parseFloat(customAmount) : null
      });
      setWorkspaces(prev => prev.map(w => w.id === ws.id
        ? { ...w, billing: { ...w.billing, sellerLimit: parseInt(sellerLimit), customAmount: customAmount ? parseFloat(customAmount) : null } } : w));
      setEditModal(null);
    } catch (e) { alert('Error guardando configuración'); }
    finally { setSaving(null); }
  };

  const trialDaysLeft = (ws) => {
    if (!ws.billing?.trialEndDate) return null;
    const end = ws.billing.trialEndDate._seconds
      ? new Date(ws.billing.trialEndDate._seconds * 1000)
      : new Date(ws.billing.trialEndDate);
    return Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  };

  const calcIngreso = (ws) => {
    if (ws.billing?.status !== 'active') return 0;
    const plan = ws.billing?.plan || 'starter';
    const features = ws.billing?.features || {};
    if (ws.billing?.customAmount) return ws.billing.customAmount;
    let total = PLAN_PRICES[plan] || 75;
    if (features.coaching) total += ADDON_PRICES.coaching;
    return total;
  };

  const totalMRR = workspaces.filter(w => w.billing?.status === 'active').reduce((sum, w) => sum + calcIngreso(w), 0);
  const totalCosto = usage.reduce((sum, u) => sum + (u.months?.[0]?.totalCost || 0), 0);
  const totalMensajes = usage.reduce((sum, u) => sum + (u.months?.[0]?.messages_outbound || 0) + (u.months?.[0]?.messages_inbound || 0), 0);
  const totalTokens = usage.reduce((sum, u) => sum + (u.months?.[0]?.tokensInput || 0) + (u.months?.[0]?.tokensOutput || 0), 0);

  const filtered = workspaces.filter(ws => {
    const matchSearch = ws.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || ws.billing?.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: workspaces.length,
    trial: workspaces.filter(w => w.billing?.status === 'trial').length,
    active: workspaces.filter(w => w.billing?.status === 'active').length,
    suspended: workspaces.filter(w => w.billing?.status === 'suspended').length,
  };

  if (loading) return (
    <div style={{ display: 'flex' }}>
      <Sidebar active="superadmin" />
      <div style={{ marginLeft: 220, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ color: '#00C853', fontSize: 14 }}>Cargando...</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#111111' }}>
      <Sidebar active="superadmin" />

      <div style={{ marginLeft: 220, flex: 1, padding: '24px' }}>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 500, color: '#FFFFFF' }}>Super Admin</div>
          <div style={{ fontSize: 12, color: '#666666', marginTop: 3 }}>Todas las empresas en Winal</div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total empresas', value: stats.total, color: '#FFFFFF' },
            { label: 'En trial', value: stats.trial, color: '#F59E0B' },
            { label: 'Activas', value: stats.active, color: '#00C853' },
            { label: 'MRR', value: `$${totalMRR}`, color: '#00C853' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#1A1A1A', borderRadius: 10, padding: '14px 16px', border: '1px solid #2A2A2A' }}>
              <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 24, fontWeight: 500, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#1A1A1A', borderRadius: 8, padding: 4, width: 'fit-content', border: '1px solid #2A2A2A' }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              background: activeTab === tab ? '#00C853' : 'transparent',
              color: activeTab === tab ? '#0A0A0A' : '#666666',
              border: 'none', borderRadius: 6, padding: '6px 18px',
              fontSize: 13, fontWeight: activeTab === tab ? 500 : 400, cursor: 'pointer',
            }}>
              {tab === 'empresas' ? '🏢 Empresas' : tab === 'costos' ? '💰 Costos' : tab === 'ingresos' ? '💵 Ingresos' : '🎯 Leads'}
            </button>
          ))}
        </div>

        {/* TAB EMPRESAS */}
        {activeTab === 'empresas' && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar empresa..."
                style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, padding: '8px 12px', color: '#FFFFFF', fontSize: 13, outline: 'none', flex: 1 }}/>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, padding: '8px 12px', color: '#FFFFFF', fontSize: 13, outline: 'none' }}>
                <option value="all">Todos los estados</option>
                <option value="trial">Trial</option>
                <option value="active">Activos</option>
                <option value="suspended">Suspendidos</option>
              </select>
            </div>

            {filtered.map(ws => {
              const daysLeft = trialDaysLeft(ws);
              const status = ws.billing?.status || 'trial';
              const plan = ws.billing?.plan || 'starter';
              const features = ws.billing?.features || {};
              const masterEnabled = ws.billing?.masterEnabled !== false;
              const isExpanded = expandedId === ws.id;
              const sellerCount = sellerCounts[ws.id] || 0;
              const sellerLimit = ws.billing?.sellerLimit ?? PLAN_SELLER_LIMITS[plan];
              const customAmount = ws.billing?.customAmount;

              return (
                <div key={ws.id} style={{ background: '#1A1A1A', borderRadius: 10, border: `1px solid ${!masterEnabled ? '#EF4444' : '#2A2A2A'}`, marginBottom: 8 }}>
                  <div onClick={() => setExpandedId(isExpanded ? null : ws.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: masterEnabled ? 'rgba(0,200,83,0.1)' : 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 500, color: masterEnabled ? '#00C853' : '#EF4444', flexShrink: 0 }}>
                      {ws.name?.slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}>{ws.name}</div>
                        <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: `${STATUS_COLORS[status]}20`, color: STATUS_COLORS[status] }}>
                          {STATUS_LABELS[status]}
                        </span>
                        <span style={{ fontSize: 10, color: '#555555', padding: '2px 8px', borderRadius: 20, background: '#111111' }}>
                          {PLAN_LABELS[plan]}
                        </span>
                        {!masterEnabled && (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
                            🔴 Suspendido por superadmin
                          </span>
                        )}
                        {customAmount && (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(123,47,190,0.1)', color: '#7B2FBE' }}>
                            Custom ${customAmount}/mes
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#555555', marginTop: 3 }}>
                        {ws.region} · {ws.currency}
                        {' · '}
                        <span style={{ color: sellerCount >= sellerLimit ? '#EF4444' : '#666666' }}>
                          {sellerCount}/{sellerLimit} vendedores
                        </span>
                        {status === 'trial' && daysLeft !== null && (
                          <span style={{ color: daysLeft <= 5 ? '#EF4444' : '#F59E0B', marginLeft: 8 }}>
                            · {daysLeft > 0 ? `${daysLeft} días de trial` : 'Trial vencido'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#555555' }}>{isExpanded ? '▲' : '▼'}</div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 18px 18px', borderTop: '1px solid #222222' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14, marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>Estado de la cuenta</div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {['trial', 'active', 'suspended'].map(s => (
                              <button key={s} onClick={() => handleStatus(ws.id, s)} style={{
                                background: status === s ? STATUS_COLORS[s] : '#111111',
                                color: status === s ? 'white' : '#666666',
                                border: `1px solid ${status === s ? STATUS_COLORS[s] : '#2A2A2A'}`,
                                borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer'
                              }}>
                                {STATUS_LABELS[s]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>Plan</div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {['starter', 'growth', 'enterprise'].map(p => (
                              <button key={p} onClick={() => handlePlan(ws.id, p)} style={{
                                background: plan === p ? '#00C853' : '#111111',
                                color: plan === p ? '#0A0A0A' : '#666666',
                                border: `1px solid ${plan === p ? '#00C853' : '#2A2A2A'}`,
                                borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer'
                              }}>
                                {PLAN_LABELS[p]}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: '#111111', borderRadius: 8, padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>Vendedores</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                            <span style={{ fontSize: 22, fontWeight: 500, color: sellerCount >= sellerLimit ? '#EF4444' : '#FFFFFF' }}>{sellerCount}</span>
                            <span style={{ fontSize: 13, color: '#555555' }}>/ {sellerLimit} límite</span>
                          </div>
                          {sellerCount >= sellerLimit && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 4 }}>⚠️ Límite alcanzado</div>}
                        </div>
                        <div style={{ background: '#111111', borderRadius: 8, padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>Facturación</div>
                          <div style={{ fontSize: 18, fontWeight: 500, color: customAmount ? '#7B2FBE' : '#00C853' }}>
                            ${customAmount || calcIngreso(ws)}<span style={{ fontSize: 11, color: '#555555' }}>/mes</span>
                          </div>
                          {customAmount && <div style={{ fontSize: 11, color: '#7B2FBE', marginTop: 2 }}>Monto custom</div>}
                        </div>
                      </div>

                      <button onClick={() => setEditModal({ ws, sellerLimit: sellerLimit.toString(), customAmount: customAmount?.toString() || '' })}
                        style={{ background: 'transparent', border: '1px solid #2A2A2A', borderRadius: 7, padding: '6px 14px', fontSize: 12, color: '#888888', cursor: 'pointer', marginBottom: 14 }}>
                        ✏️ Editar límite de vendedores y monto custom
                      </button>

                      <div style={{ fontSize: 11, color: '#666666', marginBottom: 8 }}>Módulos habilitados</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        {[
                          { key: 'hubspot', label: 'HubSpot CRM', desc: 'Sync automático de contactos y deals' },
                          { key: 'slack', label: 'Slack', desc: 'Notificaciones en canal de ventas' },
                          { key: 'coaching', label: 'Coach IA', desc: 'Coach automático al vendedor' },
                        ].map(f => {
                          const isOn = features[f.key] || false;
                          return (
                            <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#111111', borderRadius: 8, padding: '10px 12px' }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 500, color: '#FFFFFF' }}>{f.label}</div>
                                <div style={{ fontSize: 10, color: '#555555', marginTop: 2 }}>{f.desc}</div>
                              </div>
                              <Toggle on={isOn} onClick={() => handleFeature(ws.id, f.key, !isOn)} disabled={saving === ws.id + f.key} />
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ marginTop: 14, padding: '12px 14px', background: masterEnabled ? 'rgba(0,200,83,0.03)' : 'rgba(239,68,68,0.05)', borderRadius: 8, border: `1px solid ${masterEnabled ? '#1E1E1E' : 'rgba(239,68,68,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: masterEnabled ? '#FFFFFF' : '#EF4444' }}>Switch maestro</div>
                          <div style={{ fontSize: 11, color: '#555555', marginTop: 2 }}>
                            {masterEnabled ? 'Cuenta activa — acceso habilitado' : '⚠️ Cuenta suspendida — sin acceso a dashboard ni WhatsApp'}
                          </div>
                        </div>
                        <Toggle on={masterEnabled} onClick={() => handleMasterSwitch(ws.id, !masterEnabled)} disabled={saving === ws.id + 'master'} />
                      </div>

                      {status === 'trial' && (
                        <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(245,158,11,0.05)', borderRadius: 8, border: '1px solid rgba(245,158,11,0.2)' }}>
                          <div style={{ fontSize: 12, color: '#F59E0B' }}>
                            Trial: {ws.billing?.trialStartDate?._seconds ? new Date(ws.billing.trialStartDate._seconds * 1000).toLocaleDateString() : '—'}
                            {' → '}
                            {ws.billing?.trialEndDate?._seconds ? new Date(ws.billing.trialEndDate._seconds * 1000).toLocaleDateString() : '—'}
                            {daysLeft !== null && <span style={{ marginLeft: 8, fontWeight: 500 }}>({daysLeft > 0 ? `${daysLeft} días restantes` : 'Vencido'})</span>}
                          </div>
                          <button onClick={() => handleStatus(ws.id, 'active')} style={{ marginTop: 8, background: '#00C853', color: '#0A0A0A', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                            Activar cuenta →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#555555', fontSize: 13, padding: 40 }}>
                No hay empresas que coincidan con el filtro
              </div>
            )}
          </>
        )}

        {/* TAB COSTOS */}
        {activeTab === 'costos' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: '#666666' }}>Mes:</div>
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
                style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, padding: '6px 12px', color: '#FFFFFF', fontSize: 13, outline: 'none' }}/>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Costo total del mes', value: `$${totalCosto.toFixed(2)} USD`, color: '#EF4444', sub: 'Twilio + Claude' },
                { label: 'Mensajes totales', value: totalMensajes.toLocaleString(), color: '#00C853', sub: 'Enviados + recibidos' },
                { label: 'Tokens Claude', value: totalTokens.toLocaleString(), color: '#7B2FBE', sub: 'Input + output' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#1A1A1A', borderRadius: 10, padding: '16px', border: '1px solid #2A2A2A' }}>
                  <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 500, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#555555', marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ background: '#1A1A1A', borderRadius: 10, border: '1px solid #2A2A2A', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: '1px solid #2A2A2A', fontSize: 11, color: '#555555', fontWeight: 500, textTransform: 'uppercase' }}>
                <div>Empresa</div>
                <div style={{ textAlign: 'right' }}>Msgs enviados</div>
                <div style={{ textAlign: 'right' }}>Msgs recibidos</div>
                <div style={{ textAlign: 'right' }}>Tokens</div>
                <div style={{ textAlign: 'right' }}>Costo msgs</div>
                <div style={{ textAlign: 'right' }}>Costo total</div>
              </div>
              {usage.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#555555', fontSize: 13 }}>Sin datos de uso para este mes</div>
              ) : usage.map((u, i) => {
                const data = u.months?.[0] || {};
                const tokens = (data.tokensInput || 0) + (data.tokensOutput || 0);
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '12px 16px', borderBottom: i < usage.length - 1 ? '1px solid #1A1A1A' : 'none', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#FFFFFF' }}>{u.workspaceName}</div>
                      <div style={{ fontSize: 11, color: '#555555' }}>{u.workspaceId?.slice(0,8)}...</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13, color: '#FFFFFF' }}>{data.messages_outbound || 0}</div>
                    <div style={{ textAlign: 'right', fontSize: 13, color: '#FFFFFF' }}>{data.messages_inbound || 0}</div>
                    <div style={{ textAlign: 'right', fontSize: 13, color: '#7B2FBE' }}>{tokens.toLocaleString()}</div>
                    <div style={{ textAlign: 'right', fontSize: 13, color: '#555555' }}>${(data.costMessages || 0).toFixed(3)}</div>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 500, color: data.totalCost > 1 ? '#EF4444' : '#00C853' }}>
                      ${(data.totalCost || 0).toFixed(3)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: '#555555' }}>
              💡 Costos estimados: Twilio $0.005/msg · Claude input $0.003/1k tokens · Claude output $0.015/1k tokens
            </div>
          </>
        )}

        {/* TAB INGRESOS */}
        {activeTab === 'ingresos' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'MRR total', value: `$${totalMRR} USD`, color: '#00C853', sub: 'Solo cuentas activas' },
                { label: 'Costo infra estimado', value: `$${totalCosto.toFixed(2)} USD`, color: '#EF4444', sub: 'Twilio + Claude' },
                { label: 'Margen estimado', value: totalMRR > 0 ? `${Math.round(((totalMRR - totalCosto) / totalMRR) * 100)}%` : '—', color: '#00C853', sub: 'Bruto sin Railway/infra fija' },
                { label: 'ARR proyectado', value: `$${totalMRR * 12} USD`, color: '#7B2FBE', sub: 'MRR × 12' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#1A1A1A', borderRadius: 10, padding: '16px', border: '1px solid #2A2A2A' }}>
                  <div style={{ fontSize: 11, color: '#666666', marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 500, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#555555', marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ background: '#1A1A1A', borderRadius: 10, border: '1px solid #2A2A2A', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: '1px solid #2A2A2A', fontSize: 11, color: '#555555', fontWeight: 500, textTransform: 'uppercase' }}>
                <div>Empresa</div>
                <div style={{ textAlign: 'right' }}>Estado</div>
                <div style={{ textAlign: 'right' }}>Plan</div>
                <div style={{ textAlign: 'right' }}>Vendedores</div>
                <div style={{ textAlign: 'right' }}>Add-ons</div>
                <div style={{ textAlign: 'right' }}>Ingreso/mes</div>
                <div style={{ textAlign: 'right' }}>Margen</div>
              </div>
              {[...workspaces].sort((a, b) => calcIngreso(b) - calcIngreso(a)).map((ws, i) => {
                const plan = ws.billing?.plan || 'starter';
                const features = ws.billing?.features || {};
                const status = ws.billing?.status || 'trial';
                const ingreso = calcIngreso(ws);
                const usageData = usage.find(u => u.workspaceId === ws.id)?.months?.[0];
                const costo = usageData?.totalCost || 0;
                const margen = ingreso > 0 ? Math.round(((ingreso - costo) / ingreso) * 100) : null;
                const sellerCount = sellerCounts[ws.id] || 0;
                const sellerLimit = ws.billing?.sellerLimit ?? PLAN_SELLER_LIMITS[plan];
                const addonsActivos = [
                  features.coaching && 'Coach IA +$29',
                ].filter(Boolean);
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '12px 16px', borderBottom: i < workspaces.length - 1 ? '1px solid #161616' : 'none', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#FFFFFF' }}>{ws.name}</div>
                      <div style={{ fontSize: 11, color: '#555555' }}>{ws.region} · {ws.currency}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: `${STATUS_COLORS[status]}20`, color: STATUS_COLORS[status], fontWeight: 500 }}>
                        {STATUS_LABELS[status]}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#FFFFFF' }}>
                      {PLAN_LABELS[plan]}<br/>
                      <span style={{ fontSize: 10, color: '#555555' }}>${PLAN_PRICES[plan]}/mes</span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, color: sellerCount >= sellerLimit ? '#EF4444' : '#AAAAAA' }}>
                      {sellerCount}/{sellerLimit}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11 }}>
                      {addonsActivos.length > 0
                        ? addonsActivos.map((a, j) => <div key={j} style={{ color: '#00C853' }}>{a}</div>)
                        : <span style={{ color: '#333333' }}>—</span>
                      }
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, color: ingreso > 0 ? '#00C853' : '#555555' }}>
                      {ingreso > 0 ? `$${ingreso} USD` : '—'}
                      {ws.billing?.customAmount && <div style={{ fontSize: 10, color: '#7B2FBE' }}>custom</div>}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 500, color: margen !== null ? (margen >= 80 ? '#00C853' : '#F59E0B') : '#555555' }}>
                      {margen !== null ? `${margen}%` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: '#555555' }}>
              💡 El margen no incluye costos fijos de Railway (~$5/mes) ni dominio. Las cuentas en trial no generan ingreso.
            </div>
          </>
        )}

        {/* TAB LEADS */}
        {activeTab === 'leads' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#666666' }}>
                {demoLeads.length} lead{demoLeads.length !== 1 ? 's' : ''} desde el formulario de la landing
              </div>
              <button onClick={loadDemoLeads} style={{ background: 'transparent', border: '1px solid #2A2A2A', borderRadius: 7, padding: '5px 12px', fontSize: 12, color: '#666666', cursor: 'pointer' }}>
                ↻ Actualizar
              </button>
            </div>
            {demoLeads.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#555555', fontSize: 13, padding: 40, background: '#1A1A1A', borderRadius: 10, border: '1px solid #2A2A2A' }}>
                Sin leads todavía — cuando alguien complete el formulario de demo aparecerá acá
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {demoLeads.map((lead) => (
                  <div key={lead.id} style={{ background: '#1A1A1A', borderRadius: 10, border: '1px solid #2A2A2A', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(0,200,83,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👤</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}>{lead.name || 'Sin nombre'}</div>
                      <div style={{ fontSize: 13, color: '#00C853' }}>{lead.email}</div>
                      <div style={{ fontSize: 11, color: '#555555', marginTop: 2 }}>
                        {lead.createdAt?._seconds
                          ? new Date(lead.createdAt._seconds * 1000).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </div>
                      {lead.phone && <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>📱 {lead.phone}</div>}
                      {lead.comment && <div style={{ fontSize: 12, color: '#888888', marginTop: 4, fontStyle: 'italic' }}>"{lead.comment}"</div>}
                      {lead.notes && <div style={{ fontSize: 12, color: '#555555', marginTop: 4 }}>📝 {lead.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                      <select value={lead.status || 'new'} onChange={e => updateDemoLead(lead.id, { status: e.target.value })}
                        style={{ background: '#111111', border: `1px solid ${LEAD_STATUS_COLORS[lead.status || 'new']}60`, borderRadius: 6, padding: '5px 10px', color: LEAD_STATUS_COLORS[lead.status || 'new'], fontSize: 12, outline: 'none', cursor: 'pointer' }}>
                        {Object.entries(LEAD_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <input
                        placeholder="Agregar nota..."
                        defaultValue={lead.notes || ''}
                        onBlur={e => { if (e.target.value !== (lead.notes || '')) updateDemoLead(lead.id, { notes: e.target.value }); }}
                        style={{ background: '#111111', border: '1px solid #2A2A2A', borderRadius: 6, padding: '5px 10px', color: '#AAAAAA', fontSize: 11, outline: 'none', width: 180 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL — Editar límite custom */}
      <Modal show={!!editModal} onClose={() => setEditModal(null)}>
        <div style={{ fontSize: 16, fontWeight: 500, color: '#FFFFFF', marginBottom: 4 }}>
          Configuración custom — {editModal?.ws.name}
        </div>
        <div style={{ fontSize: 12, color: '#666666', marginBottom: 20 }}>
          Ajustá el límite de vendedores y el monto a cobrar si es un acuerdo especial.
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#888888', marginBottom: 6 }}>Límite de vendedores</div>
          <input type="number" min="1" max="9999"
            value={editModal?.sellerLimit || ''}
            onChange={e => setEditModal(prev => ({ ...prev, sellerLimit: e.target.value }))}
            style={{ width: '100%', background: '#111111', border: '1px solid #2A2A2A', borderRadius: 8, padding: '10px 12px', color: '#FFFFFF', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            placeholder="Ej: 25"
          />
          <div style={{ fontSize: 11, color: '#444444', marginTop: 4 }}>Estándar: Starter 5 · Growth 10 · Enterprise 20</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: '#888888', marginBottom: 6 }}>Monto custom mensual (USD)</div>
          <input type="number" min="0"
            value={editModal?.customAmount || ''}
            onChange={e => setEditModal(prev => ({ ...prev, customAmount: e.target.value }))}
            style={{ width: '100%', background: '#111111', border: '1px solid #2A2A2A', borderRadius: 8, padding: '10px 12px', color: '#FFFFFF', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            placeholder="Dejá vacío para usar precio del plan"
          />
          <div style={{ fontSize: 11, color: '#444444', marginTop: 4 }}>Si lo completás, reemplaza el precio estándar del plan + add-ons.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleEditCustom} disabled={saving === editModal?.ws.id + 'custom'}
            style={{ flex: 1, background: '#00C853', color: '#0A0A0A', border: 'none', borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
          <button onClick={() => setEditModal(null)}
            style={{ background: 'transparent', border: '1px solid #2A2A2A', borderRadius: 8, padding: '11px 18px', fontSize: 13, color: '#777777', cursor: 'pointer' }}>
            Cancelar
          </button>
        </div>
      </Modal>
    </div>
  );
}